import { Temporal } from "temporal-polyfill";
import type {
  TravelMode,
  TravelRouteResult,
  TravelRouteSegmentRequest,
  ZonedTimeString,
} from "@poi-planner/shared";
import type { Coords } from "../utils/geo";
import { api } from "./api";

// ── Travel route resolution ──
// Batches Google Routes lookups from every visible day column into as few
// POST /api/travel/routes calls as possible, and memoizes results for the
// session. Callers get the offline estimate until a result arrives.

/** Matches the server's per-request cap. */
export const TRAVEL_ROUTES_BATCH_SIZE = 20;

/** ~11m: nearby pins share a lookup, mirroring the server's edge cache. */
const COORD_DECIMALS = 4;

export interface TravelRouteQuery {
  origin: Coords;
  destination: Coords;
  mode: TravelMode;
  departureTime: ZonedTimeString;
}

type Resolved = Omit<TravelRouteResult, "key">;

const results = new Map<string, Resolved>();
const inFlight = new Map<string, Promise<Resolved>>();

let queue: Array<{
  request: TravelRouteSegmentRequest;
  resolve: (value: Resolved) => void;
}> = [];
let flushScheduled = false;

function toInstant(zoned: ZonedTimeString): string | undefined {
  try {
    return Temporal.ZonedDateTime.from(zoned).toInstant().toString();
  } catch {
    return undefined;
  }
}

/**
 * Session cache key. Transit also keys on the departure hour because its
 * duration follows the timetable; walking and driving don't.
 */
export function travelRouteKey(query: TravelRouteQuery): string {
  const round = (value: number) => value.toFixed(COORD_DECIMALS);
  const origin = `${round(query.origin.lat)},${round(query.origin.lng)}`;
  const destination = `${round(query.destination.lat)},${round(query.destination.lng)}`;
  const hour =
    query.mode === "transit"
      ? (toInstant(query.departureTime)?.slice(0, 13) ?? "any")
      : "any";
  return `${query.mode}|${origin}|${destination}|${hour}`;
}

async function flush(): Promise<void> {
  flushScheduled = false;
  const batch = queue;
  queue = [];

  for (let i = 0; i < batch.length; i += TRAVEL_ROUTES_BATCH_SIZE) {
    const chunk = batch.slice(i, i + TRAVEL_ROUTES_BATCH_SIZE);
    try {
      const response = await api.computeTravelRoutes(
        chunk.map((entry) => entry.request),
      );
      const byKey = new Map(response.map((result) => [result.key, result]));
      for (const { request, resolve } of chunk) {
        const result = byKey.get(request.key);
        const resolved: Resolved = result
          ? {
              status: result.status,
              durationMinutes: result.durationMinutes,
              distanceMeters: result.distanceMeters,
            }
          : { status: "unavailable" };
        results.set(request.key, resolved);
        inFlight.delete(request.key);
        resolve(resolved);
      }
    } catch (error: unknown) {
      // Network/auth failure: fall back to estimates now, but don't memoize
      // so a later render can retry.
      console.warn("Travel route lookup failed", error);
      for (const { request, resolve } of chunk) {
        inFlight.delete(request.key);
        resolve({ status: "unavailable" });
      }
    }
  }
}

/** A result already known this session, if any (no request is made). */
export function peekTravelRoute(query: TravelRouteQuery): Resolved | null {
  return results.get(travelRouteKey(query)) ?? null;
}

/**
 * Resolves a route, joining an identical in-flight lookup and batching with
 * every other request made in the same tick.
 */
export function requestTravelRoute(query: TravelRouteQuery): Promise<Resolved> {
  const key = travelRouteKey(query);
  const known = results.get(key);
  if (known) return Promise.resolve(known);
  const pending = inFlight.get(key);
  if (pending) return pending;

  const promise = new Promise<Resolved>((resolve) => {
    queue.push({
      request: {
        key,
        origin: query.origin,
        destination: query.destination,
        mode: query.mode,
        departureTime:
          query.mode === "transit" ? toInstant(query.departureTime) : undefined,
      },
      resolve,
    });
  });
  inFlight.set(key, promise);

  if (!flushScheduled) {
    flushScheduled = true;
    // A macrotask, not a microtask, so every day column's effect in the same
    // commit lands in one batch.
    setTimeout(() => void flush(), 0);
  }
  return promise;
}

/** Resolved routes worth keeping on the device for offline travel pins. */
export function exportTravelRoutes(limit = 500): Array<[string, Resolved]> {
  return [...results]
    .filter(([, route]) => route.status === "ok")
    .slice(-limit);
}

/** Seed the session cache from routes saved with an offline trip copy. */
export function seedTravelRoutes(entries: Iterable<[string, Resolved]>): void {
  for (const [key, route] of entries) {
    if (!results.has(key)) results.set(key, route);
  }
}

/** Test hook: forget every memoized and pending lookup. */
export function resetTravelRouteCache(): void {
  results.clear();
  inFlight.clear();
  queue = [];
  flushScheduled = false;
}
